import { useEffect } from 'react'
import { Button, Header, Icon, Loader, Popup } from 'semantic-ui-react'
import { gql, useMutation } from '@apollo/client'
import router from 'next/router'

import styles from './style.module.css'
import { getApplicationLabels } from '@components/MobileApplicationCard'
import { createApplication } from '@src/graphql/mutations'
import { getApplicationDateForJobPostId } from '@src/helpers'
import { getJobApplicationsForUser } from 'queries'
import parse from '@src/helpers/parseHTML'
import { useAuth } from '@src/auth'
import Logo from '../Logo'
import SponsorDetails from '../SponsorDetails'
import LocationSummary from '../LocationSummary'
import HybridSummary from '../HybridSummary'
import RoleDetailsSummary from '../RoleDetailsSummary'
import DigitalNomadBadge from '../DigitalNomadBadge'

import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  RedditShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from 'react-share'

import {
  EmailIcon,
  FacebookIcon,
  LinkedinIcon,
  RedditIcon,
  TwitterIcon,
  WhatsappIcon,
} from 'react-share'
import { renderDatePostedForPost } from '@src/helpers/jobsearch'

export default function CardDetail({
  applications,
  idx,
  postings,
  jobSeekerId,
  showApplicationDate,
  status,
  innerRef,
}) {
  const [apply, { data: updateData, loading, error, reset }] = useMutation(
    gql(createApplication),
    {
      refetchQueries: [getJobApplicationsForUser],
    },
  )

  const { isEnterprise, user } = useAuth()

  useEffect(() => {
    reset()
  }, [updateData])

  if (idx > -1 && postings[idx]) {
    const {
      aboutUs,
      description,
      jobTitle,
      companyId,
      companyName,
      companyProfile,
      applied,
      jobLogo,
      scrapedUrl,
      scrapedSource,
    } = postings[idx]
    const posting = postings[idx]
    const applicationDate =
      getApplicationDateForJobPostId(applications, postings[idx].id) ||
      postings[idx]?.dateApplied

    const applyToJob = () => {
      if (jobSeekerId) {
        const vars = {
          variables: {
            input: {
              id: (Math.random() * 1000).toString(),
              jobId: posting?.id,
              jobSeekerId,
            },
          },
        }

        apply(vars)
      } else {
        router.push('/signup')
      }
    }

    const ShareContent = ({ copyOnly }) => {
      const openPop = () => {
        navigator.clipboard.writeText(
          `www.lifeshack.com/job/${postings[idx].id}`,
        )
      }

      const titleString = `${jobTitle} @  ${companyName} - now hiring!`
      const summaryString = `Find the best remote and hybrid jobs on LifeShack.com`
      const sourceString = `www.LifeShack.com`

      if (copyOnly) {
        return (
          <Popup
            content="Link copied!"
            on="click"
            onOpen={openPop}
            trigger={
              <div className={styles.shareWrap}>
                <div
                  className={styles.webShare}
                  style={{ width: '28px', height: '28px', marginTop: '2px' }}
                >
                  <Icon name="linkify" />
                </div>
              </div>
            }
          />
        )
      }

      return (
        <div className={styles.shareLinks}>
          <Popup
            content="Link copied!"
            on="click"
            onOpen={openPop}
            trigger={
              <div className={styles.shareWrap}>
                <div className={styles.webShare}>
                  <Icon name="linkify" />
                </div>
              </div>
            }
          />

          <div className={styles.shareWrap}>
            <LinkedinShareButton
              title={titleString}
              summary={summaryString}
              source={sourceString}
              url={`www.lifeshack.com/job/${postings[idx].id}`}
            >
              <LinkedinIcon round={true} size={33} />
            </LinkedinShareButton>
          </div>

          <div className={styles.shareWrap}>
            <FacebookShareButton
              quote={titleString}
              hashtag={`#remotework`}
              url={`www.lifeshack.com/job/${postings[idx].id}`}
            >
              <FacebookIcon round={true} size={33} />
            </FacebookShareButton>
          </div>

          <div className={styles.shareWrap}>
            <RedditShareButton
              title={titleString}
              url={`www.lifeshack.com/job/${postings[idx].id}`}
            >
              <RedditIcon round={true} size={33} />
            </RedditShareButton>
          </div>

          <div className={styles.shareWrap}>
            <TwitterShareButton
              title={titleString}
              via={'LifeShack.com'}
              hashtags={['remotejobs', 'remotework', 'hiring']}
              url={`www.lifeshack.com/job/${postings[idx].id}`}
            >
              <TwitterIcon round={true} size={33} />
            </TwitterShareButton>
          </div>

          <div className={styles.shareWrap}>
            <EmailShareButton
              title={titleString}
              body={`Check out this remote job on LifeShack.com`}
              url={`www.lifeshack.com/job/${postings[idx].id}`}
            >
              <EmailIcon round={true} size={33} />
            </EmailShareButton>
          </div>

          <div className={styles.shareWrap}>
            <WhatsappShareButton
              title={titleString}
              url={`www.lifeshack.com/job/${postings[idx].id}`}
            >
              <WhatsappIcon round={true} size={33} />
            </WhatsappShareButton>
          </div>
        </div>
      )
    }

    const renderApplyNow = (withCopyButton) => {
      if (isEnterprise()) return null

      if (status === 'ACCEPTED') {
        return (
          <Header as="h4" className={styles.accepted}>
            Application Accepted
          </Header>
        )
      } else if (status === 'DECLINED') {
        return (
          <Header as="h4" className={styles.declined}>
            Application Declined
          </Header>
        )
      }

      if (applied || applicationDate) {
        return (
          <div>
            <Button circular disabled color="blue" size="mini">
              Applied{' '}
              <Icon
                name="check"
                style={{ marginLeft: '8px', marginRight: '0' }}
              />
            </Button>
          </div>
        )
      } else if (loading) {
        return (
          <div>
            <Button
              circular
              color="blue"
              disabled
              onClick={applyToJob}
              size="mini"
            >
              Apply Now{' '}
              <Loader
                size="mini"
                inverted
                active
                inline
                style={{ marginLeft: '8px', marginRight: '0' }}
              ></Loader>
            </Button>
          </div>
        )
      }

      if (scrapedUrl) {
        return (
          <div className={styles.applyContainer}>
            <a target="_blank" href={scrapedUrl} rel="noopener noreferrer">
              <Button circular color="blue" size="mini">
                Apply Now{' '}
                <Icon
                  name="location arrow"
                  style={{ marginLeft: '8px', marginRight: '0' }}
                />
              </Button>
            </a>
            {withCopyButton && <ShareContent copyOnly />}
          </div>
        )
      }
      return (
        <div className={styles.applyContainer}>
          <div className={styles.applyNowButton}>
            <Button circular color="blue" onClick={applyToJob} size="mini">
              Apply Now{' '}
              <Icon
                name="location arrow"
                style={{ marginLeft: '8px', marginRight: '0' }}
              />
            </Button>
          </div>
          {withCopyButton && <ShareContent copyOnly />}
        </div>
      )
    }

    const renderAboutUs = () => {
      if (aboutUs && aboutUs?.length) {
        return (
          <div className={styles.aboutUs}>
            <div className={styles.header}>
              <Header as="h3">About Us</Header>
            </div>
            {aboutUs}
          </div>
        )
      }
      return null
    }
    
    let text = description

    if (description?.length) {
      text = description.replaceAll(
        '<p dataSomething="min-height: 1.5em;"> </p>',
        '',
      )
    }

    let customClass

    if (scrapedSource === 'remotiveio') {
      customClass = styles.remotive
    }

    if (scrapedSource === 'yc') {
      customClass = styles.yc
    }

    if (!scrapedSource) {
      customClass = styles.ls
    }

    return (
      <div className={`${styles.cardDetail} cardDetail`}>
        <div className={styles.topRow}>
          <div className={styles.logoWrapper}>
            <Logo
              companyName={companyName}
              companyId={companyId}
              image={jobLogo ?? companyProfile?.image}
              wrapperStyle={{ width: '220px ' }}
              nameStyle={{ fontSize: '17px', fontWeight: 'bold' }}
              large
            />
          </div>
          <div className={styles.detailTitle}>
            <Header as="h1">{jobTitle}</Header>
            <div className={styles.datePosted}>
              {renderDatePostedForPost(posting)}
            </div>
            <div className={styles.applyNow}>
              <div className={styles.applyWrapper}>{renderApplyNow(true)}</div>
            </div>
          </div>
          <div className={styles.detailTags}>
            {getApplicationLabels(posting, false, true)}
          </div>
        </div>
        <div className={styles.jobDescriptionFull} ref={innerRef}>
          <div className={`${styles.jdText} ${customClass}`}>
            {parse(text)}
            {renderAboutUs()}
          </div>
        </div>
      </div>
    )
  }
  return null
}
